/** What a month in a band's bars opens: the "Every figure" drawer for that month - design uplift U5.
 *
 * A month used to navigate to the Emissions screen with the period selector rewritten to one month,
 * which changed the page's context silently. It now opens the same drawer the total tile opens, on
 * the page the reader is on, with the period unchanged: the key `month-YYYY-MM` goes in the URL's
 * `breakdown`, and the page resolves it to the figures that overlap that month (`/figures`, a page at
 * a time) and to the server's own total for the month (`/inventory/monthly`), never a sum of rows.
 *
 * The month is clipped to the reporting window, so a window that starts or ends inside a month asks
 * for the part of the month it holds, which is what the bar drew.
 */

import type { FigureCut, FigureCutRead, InventoryMonth, Window } from "../../api/queries";
import { useFiguresPaged } from "../../api/queries";
import { monthLabel } from "../../lib/format";
import { pagingOf, WHOLE_RECORDS_NOTE } from "../drawerFigures";
import type { ScopeKey } from "./bandModel";

/** A calendar month's first and last day, written from the key's own parts so no time zone is involved. */
export function monthBounds(month: string): { from: string; to: string } {
  const year = Number(month.slice(0, 4));
  const number = Number(month.slice(5, 7));
  const last = new Date(Date.UTC(year, number, 0)).getUTCDate();
  return { from: `${month}-01`, to: `${month}-${String(last).padStart(2, "0")}` };
}

const KEY = /^month-(\d{4}-(?:0[1-9]|1[0-2]))$/;

export function monthBreakdownKey(month: string): string {
  return `month-${month}`;
}

/** The `YYYY-MM` a breakdown key names, or undefined when it names something else. */
export function monthOfBreakdown(key: string | undefined): string | undefined {
  return key ? KEY.exec(key)?.[1] : undefined;
}

/** The month's dates inside the window: the later start and the earlier end. ISO dates compare as
 * strings. Undefined when the month lies wholly outside the window (a key typed by hand). */
export function monthWindow(month: string, window: Window): Window | undefined {
  const { from, to } = monthBounds(month);
  const start = from > window.period_start ? from : window.period_start;
  const end = to < window.period_end ? to : window.period_end;
  return start <= end ? { period_start: start, period_end: end } : undefined;
}

/** The props `ScreenFrame` takes for a drawer on a month. */
export interface MonthDrawer {
  breakdownFigures: FigureCutRead["figures"];
  breakdownLabel: string;
  breakdownRead: FigureCutRead;
  breakdownPaging: ReturnType<typeof pagingOf>;
  breakdownHeadline: string | undefined;
  breakdownNote: string | undefined;
}

/** The month drawer for the page's open breakdown, or undefined when it is not a month.
 *
 * `scope` narrows the figures to a scope page's own scope (and its label). `months` is the monthly
 * series the page already read; the headline is the server's total for the month there (the scope's
 * own cell for a scope page), and is absent until the series has answered or when the month is not in
 * it, so the drawer prints no total rather than one added up here. `read` is `false` while the page's
 * key is not a month, so no request is made. */
export function useMonthDrawer(args: {
  key: string | undefined;
  window: Window | undefined;
  method: "location" | "market";
  scope?: ScopeKey;
  months: readonly InventoryMonth[] | undefined;
  /** What to call the drawer before the month: `Every figure` or `Scope 1`. */
  label: string;
}): MonthDrawer | undefined {
  const month = monthOfBreakdown(args.key);
  const window = month && args.window ? monthWindow(month, args.window) : undefined;
  const cut: FigureCut = args.scope ? { scope: args.scope } : {};
  const read = useFiguresPaged(window, args.method, cut);
  if (!month || !window) return undefined;
  const found = args.months?.find((candidate) => candidate.month === month);
  const headline = found
    ? args.scope
      ? found.by_scope.find((cell) => cell.scope === args.scope && cell.assessed)?.kgco2e
      : found.kgco2e
    : undefined;
  return {
    breakdownFigures: read.figures,
    breakdownLabel: `${args.label} · ${monthLabel(month)}`,
    breakdownRead: read,
    breakdownPaging: pagingOf(read),
    breakdownHeadline: headline,
    breakdownNote: headline !== undefined ? WHOLE_RECORDS_NOTE : undefined,
  };
}
